import React from 'react';
import ReactDOM from 'react-dom/client';
import axios from 'axios';
import { attachVisitorVerification, ensureVisitorSession } from './utils/visitorVerification';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { HelmetProvider } from 'react-helmet-async';

import "react-toastify/dist/ReactToastify.css"
import 'react-loading-skeleton/dist/skeleton.css'
import {SkeletonTheme} from "react-loading-skeleton";

// Without this, a stalled connection (common on weak mobile LTE) leaves
// api.get(...) calls pending forever - the request never resolves or
// rejects, so loading state never clears and the UI is stuck on its
// skeleton indefinitely instead of surfacing the existing error/retry UI.
// axios.create() (see useAxios.js) merges with these defaults at creation
// time, so this one line covers both that instance and every plain
// axios.* call across the app.
axios.defaults.timeout = 20000;

// API answers only browsers that passed Cloudflare Turnstile (see utils/visitorVerification.js).
// Covers every plain axios.* call; useAxios.js attaches the same to its own instance.
attachVisitorVerification(axios);
ensureVisitorSession().catch(() => {});

// Unregister any leftover Service Worker from an old deploy (this app never
// registers one itself) - cheap, one-time, doesn't touch the browser's own
// HTTP/CacheStorage caching. Unlike the unconditional caches.delete() this
// used to run alongside on every single page load: that wiped the browser's
// entire CacheStorage on every visit, not just after a real deploy, which
// made every repeat visit pay for a full re-fetch of everything for no
// reason - the version-check below already handles the actual stale-bundle
// problem precisely (only reloads when the served build has actually
// changed), so the blanket clear wasn't buying anything but the slowdown.
if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (let registration of registrations) {
            registration.unregister();
        }
    }).catch(() => {});
}

// iOS Safari caches index.html itself, so after a deploy it can keep loading
// the old document (and therefore the old JS bundle) until the user manually
// clears cache. fetch(..., {cache: 'no-store'}) bypasses that cache, so we
// use it to detect a version bump and force-navigate to a cache-busted URL,
// which the browser must fetch fresh instead of serving the stale document.
if (process.env.NODE_ENV === 'production') {
    if (window.location.search.includes('_v=')) {
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('_v');
        window.history.replaceState({}, '', cleanUrl.toString());
    }
    const checkVersion = () => {
        if ('fetch' in window) {
            fetch(`/build-meta.json?t=${Date.now()}`, {
                cache: 'no-store',
                headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate', 'Pragma': 'no-cache' }
            })
                .then((res) => (res.ok ? res.json() : null))
                .then((data) => {
                    if (!data || !data.version) return;
                    const storedVersion = localStorage.getItem('app-build-version');
                    const reloadGuard = sessionStorage.getItem('app-reload-guard');
                    if (storedVersion && storedVersion !== data.version && reloadGuard !== data.version) {
                        sessionStorage.setItem('app-reload-guard', data.version);
                        localStorage.setItem('app-build-version', data.version);
                        try { sessionStorage.clear(); } catch (e) {}
                        const url = new URL(window.location.href);
                        url.searchParams.set('_v', data.version);
                        window.location.replace(url.toString());
                        return;
                    }
                    localStorage.setItem('app-build-version', data.version);
                })
                .catch(() => {});
        }
    };

    checkVersion();
    window.addEventListener('pageshow', (e) => {
        if (e.persisted) checkVersion();
    });

    // A tab backgrounded for a long stretch (screen locked mid-tournament,
    // browser switched away for a while) can come back looking fully
    // rendered but stop responding to taps - Chrome's background tab
    // freezing doesn't reliably resume every event listener/socket the app
    // had running. There's no way to detect that broken state from inside
    // the frozen page itself, so instead of trying to patch each listener
    // back to life, a long-enough hidden spell just forces a real reload on
    // return - the one thing guaranteed to leave a fresh, responsive page,
    // independent of whether the build version actually changed.
    const LONG_BACKGROUND_RELOAD_MS = 15 * 60 * 1000;
    let hiddenAt = null;
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            hiddenAt = Date.now();
            return;
        }
        if (hiddenAt && Date.now() - hiddenAt > LONG_BACKGROUND_RELOAD_MS) {
            hiddenAt = null;
            window.location.reload();
            return;
        }
        hiddenAt = null;
        checkVersion();
    });
}

// On-device debug console for phones with no attached desktop debugger
// (iPhone Safari has no console without a Mac's Web Inspector). Opt-in via
// ?debug=1 so it never loads for regular visitors - loads eruda from a CDN
// rather than bundling it, so there's zero cost for everyone else. Remove
// once the iOS loading issue is diagnosed.
if (window.location.search.includes('debug=1')) {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/eruda';
    script.onload = () => { if (window.eruda) window.eruda.init(); };
    document.body.appendChild(script);
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
    <HelmetProvider>
        {/* CSS vars, not fixed hex - baseColor="#f8f7fa" never adapted to the
            dark theme (index.css's [data-theme='dark'] block), where it read
            as a near-white block on a dark background instead of a skeleton.
            --body-background/--white flip appropriately in both themes and
            keep the same light-to-lighter shimmer direction in each. */}
        <SkeletonTheme baseColor="var(--body-background)" highlightColor="var(--white)">
            <App />
        </SkeletonTheme>
    </HelmetProvider>
);

reportWebVitals();
